/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 09/11/21
 */

import { DispatchCell, useJsonForms } from '@jsonforms/react';
import isEmpty from 'lodash/isEmpty';
import { FormControl, FormErrorMessage, Td } from '@chakra-ui/react';
import {
    ArrayControlProps,
    ControlElement,
    formatErrorMessage,
    getErrorAt,
    JsonSchema,
    Resolve,
} from '@jsonforms/core';
import React, { ComponentType } from 'react';
import union from 'lodash/union';
import { withDisplayName } from '@/utils/withDisplayName';
import { namedMemo } from '@/utils/namedMemo';

interface ChakraTableCellProps extends OwnPropsOfChakraTableCell {
    rootSchema: JsonSchema;
    errors: string;
    path: string;
}

interface OwnPropsOfChakraTableCell extends Pick<ArrayControlProps, 'schema' | 'enabled' | 'cells' | 'renderers'> {
    rowPath: string;
    propName?: string;
}

const withCtxToCellProps = (Component: ComponentType<ChakraTableCellProps>) =>
    withDisplayName((ownProps: OwnPropsOfChakraTableCell) => {
        const ctx = useJsonForms();

        const path =
            ownProps.rowPath + (ownProps.schema.type === 'object' && ownProps.propName ? '.' + ownProps.propName : '');
        const errors = formatErrorMessage(
            union(getErrorAt(path, ownProps.schema)({ jsonforms: { ...ctx } }).map((error) => error.message || '')),
        );

        const newProps = {
            rowPath: ownProps.rowPath,
            propName: ownProps.propName,
            schema: ownProps.schema,
            rootSchema: ctx.core?.schema || {},
            errors,
            path,
            enabled: ownProps.enabled,
            cells: ownProps.cells || ctx.cells,
            renderers: ownProps.renderers || ctx.renderers,
        };
        return <Component {...newProps} />;
    }, 'withCtxToCellProps');

const controlWithoutLabel = (scope: string): ControlElement => ({
    type: 'Control',
    scope: scope,
    label: false,
});

const ChakraTableCell = namedMemo('ChakraTableCell', (props: ChakraTableCellProps) => {
    const { errors, path, enabled, renderers, cells, schema, rootSchema, propName } = props;

    const isValid = isEmpty(errors);

    const commonCellProps = {
        path,
        enabled,
        renderers,
        cells,
    };

    return (
        <Td>
            <FormControl isInvalid={!isValid} isDisabled={!enabled}>
                {schema.properties ? (
                    <DispatchCell
                        {...commonCellProps}
                        schema={Resolve.schema(schema, `#/properties/${propName}`, rootSchema)}
                        uischema={controlWithoutLabel(`#/properties/${propName}`)}
                    />
                ) : (
                    <DispatchCell {...commonCellProps} schema={schema} uischema={controlWithoutLabel('#')} />
                )}
                <FormErrorMessage>{errors}</FormErrorMessage>
            </FormControl>
        </Td>
    );
});

export default withCtxToCellProps(ChakraTableCell);
